import { useCallback } from "react";

/**
 * Universal haptics hook.
 *
 * On native iOS (Capacitor): tries to use @capacitor/haptics if installed.
 * On Android Chrome: uses the Web Vibration API.
 * On iOS Safari / unsupported: silent no-op.
 *
 * Safe to call from anywhere — never throws, never blocks.
 */

type ImpactStyle = "light" | "medium" | "heavy" | "soft";
type NotificationStyle = "success" | "warning" | "error";

// Lazy, cached module reference so we don't repeatedly probe.
let hapticsModulePromise: Promise<any> | null = null;

const tryLoadCapacitorHaptics = (): Promise<any> | null => {
  if (typeof window === "undefined") return null;
  // Only attempt on Capacitor native shells.
  // @ts-expect-error Capacitor global is injected by the native runtime.
  const isNative = !!window.Capacitor?.isNativePlatform?.();
  if (!isNative) return null;

  if (!hapticsModulePromise) {
    // Use a variable so TS / Vite don't try to statically resolve this optional module.
    const moduleName = "@capacitor/haptics";
    hapticsModulePromise = (new Function("m", "return import(m)") as (m: string) => Promise<any>)(
      moduleName
    ).catch(() => null);
  }
  return hapticsModulePromise;
};

const webVibrate = (pattern: number | number[]) => {
  try {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(pattern);
    }
  } catch {
    // ignore
  }
};

export const useHaptics = () => {
  const impact = useCallback(async (style: ImpactStyle = "light") => {
    const mod = tryLoadCapacitorHaptics();
    if (mod) {
      try {
        const { Haptics, ImpactStyle: CapImpactStyle } = await mod;
        const styleMap: Record<ImpactStyle, any> = {
          light: CapImpactStyle.Light,
          medium: CapImpactStyle.Medium,
          heavy: CapImpactStyle.Heavy,
          soft: CapImpactStyle.Light,
        };
        await Haptics.impact({ style: styleMap[style] });
        return;
      } catch {
        // fall through to web fallback
      }
    }
    const durations: Record<ImpactStyle, number> = {
      light: 8,
      medium: 14,
      heavy: 22,
      soft: 6,
    };
    webVibrate(durations[style]);
  }, []);

  const selection = useCallback(async () => {
    const mod = tryLoadCapacitorHaptics();
    if (mod) {
      try {
        const { Haptics } = await mod;
        await Haptics.selectionStart();
        await Haptics.selectionChanged();
        await Haptics.selectionEnd();
        return;
      } catch {
        // fall through
      }
    }
    webVibrate(5);
  }, []);

  const notification = useCallback(async (type: NotificationStyle = "success") => {
    const mod = tryLoadCapacitorHaptics();
    if (mod) {
      try {
        const { Haptics, NotificationType } = await mod;
        const typeMap: Record<NotificationStyle, any> = {
          success: NotificationType.Success,
          warning: NotificationType.Warning,
          error: NotificationType.Error,
        };
        await Haptics.notification({ type: typeMap[type] });
        return;
      } catch {
        // fall through
      }
    }
    const patterns: Record<NotificationStyle, number | number[]> = {
      success: [10, 30, 10],
      warning: [15, 50, 15],
      error: [20, 60, 20, 60, 20],
    };
    webVibrate(patterns[type]);
  }, []);

  return { impact, selection, notification };
};
